<template>
    <Layout>
        <PageHeader :title="$t('module.notificationChannel')" :items="breadcrumbs" />
        <div class="card">
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col-lg-4">
                        <b-input-group>
                            <b-form-input v-model.trim="searchText" type="search" :placeholder="$t('label.search')"
                                autocomplete="off" @keyup.enter="applySearch" />
                            <b-input-group-append><b-button variant="info" @click="applySearch">
                                    <i class="mdi mdi-magnify"></i>
                                </b-button></b-input-group-append>
                        </b-input-group>
                    </div>
                </div>

                <div v-if="!status.list" class="d-flex justify-content-center p-5"><b-spinner variant="info" /></div>
                <template v-else>
                    <b-table :items="tableRows" :fields="fields" show-empty responsive no-local-sorting
                        @sort-changed="changeSort">
                        <template #cell(isActive)="{ item }">
                            <b-form-checkbox :checked="item.isActive" switch :disabled="!status.save"
                                @change="changeStatus(item, $event)">
                                {{ item.isActive ? $t('label.active') : $t('label.inactive') }}
                            </b-form-checkbox>
                        </template>
                    </b-table>
                    <div v-if="pagination.total"
                        class="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
                        <small class="text-muted mb-2 mb-md-0">{{ $tc('label.results', pagination.total,
                            [pagination.total]) }}</small>
                        <b-pagination v-model="currentPage" :total-rows="pagination.total"
                            :per-page="searchCriteria.pageSize" class="mb-0" @change="pageNavigation" />
                    </div>
                </template>
            </div>
        </div>
    </Layout>
</template>

<script>
import { mapState } from 'vuex';
import Layout from '@/views/layouts/main';
import PageHeader from '@/components/page-header';
import i18n from '@/i18n';
import { notificationChannelMethods } from '@/state/api';
import { NOTIFICATION_CHANNEL_MODULE } from '@/state/api/notificationChannel.state';

const defaultCriteria = () => ({
    pageNumber: 1,
    pageSize: 15,
    searchArray: [],
    sortBy: 'name',
    orderBy: 'asc',
});

export default {
    name: 'NotificationChannelListing',
    page: { title: i18n.t('module.notificationChannel') },
    components: { Layout, PageHeader },
    data() {
        return {
            searchText: '',
            currentPage: 1,
            searchCriteria: defaultCriteria(),
            breadcrumbs: [
                { text: i18n.t('module.communication'), href: '/communications' },
                { text: i18n.t('module.notificationChannel'), active: true },
            ],
            fields: [
                { key: 'code', label: i18n.t('label.code'), sortable: true },
                { key: 'name', label: i18n.t('label.name'), sortable: true },
                { key: 'isActive', label: i18n.t('label.status'), sortable: false, class: 'text-nowrap' },
            ],
        };
    },
    computed: {
        ...mapState(NOTIFICATION_CHANNEL_MODULE, ['status']),
        tableRows() {
            return this.$store.state.api.notificationChannel.list.data || [];
        },
        pagination() {
            return this.$store.state.api.notificationChannel.list.pagination || {};
        },
    },
    created() {
        this.loadChannels();
    },
    methods: {
        ...notificationChannelMethods,
        loadChannels() {
            return this.getAllNotificationChannel({ filter: this.searchCriteria });
        },
        applySearch() {
            this.currentPage = 1;
            this.searchCriteria = {
                ...this.searchCriteria,
                pageNumber: 1,
                searchArray: this.searchText ? [this.searchText] : []
            };
            this.loadChannels();
        },
        pageNavigation(pageNumber) {
            this.currentPage = pageNumber;
            this.searchCriteria = { ...this.searchCriteria, pageNumber };
            this.loadChannels();
        },
        changeSort({ sortBy, sortDesc }) {
            const apiFields = { code: 'code', name: 'name' };
            if (!apiFields[sortBy]) return;
            this.currentPage = 1;
            this.searchCriteria = {
                ...this.searchCriteria,
                pageNumber: 1,
                sortBy: apiFields[sortBy],
                orderBy: sortDesc ? 'desc' : 'asc'
            };
            this.loadChannels();
        },
        async changeStatus(channel, isActive) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                this.$t(isActive ? 'label.activateChannelWarning' : 'label.deactivateChannelWarning', [channel.name]),
                {
                    title: this.$t(isActive ? 'label.confirmChannelActivation' : 'label.confirmChannelDeactivation'),
                    okTitle: this.$t(isActive ? 'label.activate' : 'label.deactivate'),
                    okVariant: isActive ? 'success' : 'danger',
                    cancelTitle: this.$t('label.cancel'),
                    cancelVariant: 'outline-secondary',
                    centered: true,
                },
            );
            if (!confirmed) {
                await this.loadChannels();
                return;
            }
            await this.updateNotificationChannelStatus({ uuid: channel.uuid, isActive, filter: this.searchCriteria });
        },
    },
};
</script>
